import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';

import {
  hasRoles,
  mergeRoles,
  CREATE_PARTICIPANT_REVIEW_ROLES,
  EDIT_PARTICIPANT_REVIEW_ROLES,
  VIEW_PARTICIPANT_REVIEW_ROLES,
} from '../../../../util/RoleUtil';

import { updateReview, viewReview } from '../../../../services/ReviewService';
import { viewAllQueriesForReview, viewQuery } from '../../../../services/QueryService';

import { BackButton } from '../../../common/button/back-button/BackButton';
import { ParticipantReviewGeneralTab } from '../create/ParticipantReviewGeneralTab';
import { ParticipantQueryCreateTab } from '../create/ParticipantQueryCreateTab';
import { ParticipantQueryTable } from './ParticipantQueryTable';

import '../create/ParticipantReviewCreate.css';

export const ParticipantReviewEdit = ({
  context,
  setContext,
  navigateTo,
  showToaster,
  setSpinnerText,
  toolTipsEnabled,
}) => {
  const [activeTab, setActiveTab] = useState('');
  const [review, setReview] = useState({});
  const [query, setQuery] = useState({});
  const [queries, setQueries] = useState([]);
  const [currentScreen, setCurrentScreen] = useState('');
  const [showQueriesTab, setShowQueriesTab] = useState(false);
  const [showQueriesTable, setShowQueriesTable] = useState(true);
  const [isNew, setIsNew] = useState(false);
  const [editMode, setEditMode] = useState(true);
  const [visitNotLinked, setVisitNotLinked] = useState(false);
  const [hasQueries, setHasQueries] = useState(false);

  const processedNotificationReference = useRef(null);
  const skipNextInitialiseAfterNotification = useRef(false);

  const PARTICIPANT_REVIEW_CREATE_QUERY = 'participantReviewCreateQuery';
  const additionalContextReviewExtRef = context.additionalContext?.reviewExternalReference;

  const retrieveQueries = useCallback(
    async (username, externalReference) => {
      const [data, error] = await viewAllQueriesForReview(username, externalReference);

      if (data) {
        if (data.responseStatus === 'Success') {
          setQueries(data.viewAllQueryVOS);

          if (data.viewAllQueryVOS.length > 0) {
            setHasQueries(true);
          }

          showToaster({
            responseStatus: 'Success',
            responseMessage: 'Queries Information Retrieved',
          });
        } else {
          showToaster(data);
        }
      } else {
        showToaster({
          responseStatus: 'Error',
          responseMessage: error.message,
        });
      }

      setSpinnerText(null);
    },
    [setSpinnerText, showToaster]
  );

  const retrieveReview = useCallback(
    async (username, externalReference) => {
      const reviewExternalReference =
        typeof externalReference === 'object' ? externalReference?.reviewExternalReference : externalReference;

      const [data, error] = await viewReview(username, reviewExternalReference);

      if (data) {
        const { viewReviewVO, responseStatus } = data;

        if (responseStatus === 'Success') {
          setReview(viewReviewVO);

          if (viewReviewVO.noQueries) {
            setShowQueriesTab(false);
            setShowQueriesTable(false);
          } else if (context.activeTab === PARTICIPANT_REVIEW_CREATE_QUERY) {
            setShowQueriesTab(true);
            setShowQueriesTable(false);
          } else {
            setShowQueriesTab(false);
            setShowQueriesTable(true);
          }

          if (viewReviewVO.visitCode) {
            setVisitNotLinked(true);
          } else {
            setVisitNotLinked(false);
          }

          if (editMode) {
            retrieveQueries(username, viewReviewVO.reviewExternalReference);
          }

          showToaster({
            responseStatus: 'Success',
            responseMessage: 'Review Information Retrieved',
          });
        } else {
          showToaster(data);
        }
      } else {
        showToaster({
          responseStatus: 'Error',
          responseMessage: error.message,
        });
      }

      setSpinnerText(null);
    },
    [context.activeTab, editMode, retrieveQueries, setSpinnerText, showToaster]
  );

  const retrieveQuery = useCallback(
    async (queryExtRef) => {
      if (!queryExtRef) return;

      setSpinnerText('Retrieving Query');

      const [data, error] = await viewQuery(context.username, queryExtRef);

      if (data) {
        if (data.responseStatus === 'Success') {
          setQuery(data.viewQueryVO);

          setEditMode(true);
          setIsNew(false);

          setActiveTab(PARTICIPANT_REVIEW_CREATE_QUERY);
          setCurrentScreen(PARTICIPANT_REVIEW_CREATE_QUERY);
          setShowQueriesTab(true);
          setShowQueriesTable(false);

          showToaster({
            responseStatus: 'Success',
            responseMessage: 'Query successfully retrieved.',
          });
        } else {
          showToaster(data);
        }
      } else {
        showToaster({
          responseStatus: 'Error',
          responseMessage: error.message,
        });
      }

      setSpinnerText(null);
    },
    [context.username, setSpinnerText, showToaster]
  );

  useEffect(() => {
    const viewStack = context.viewStack || [];
    const lastItem = viewStack[viewStack.length - 1];

    if (
      context.notification?.contextType === 'Query' &&
      processedNotificationReference.current !== context.notification.contextReference
    ) {
      const contextReference = context.notification.contextReference;
      processedNotificationReference.current = contextReference;
      skipNextInitialiseAfterNotification.current = true;

      const [reviewExternalReference, queryExternalReference] = contextReference.split('$');

      setEditMode(true);
      setIsNew(false);
      setActiveTab(PARTICIPANT_REVIEW_CREATE_QUERY);
      setCurrentScreen(PARTICIPANT_REVIEW_CREATE_QUERY);
      setShowQueriesTab(true);
      setShowQueriesTable(false);

      retrieveReview(context.username, reviewExternalReference);
      retrieveQuery(queryExternalReference);

      setContext((prevState) => ({
        ...prevState,
        notification: undefined,
      }));

      return;
    }

    if (skipNextInitialiseAfterNotification.current) {
      skipNextInitialiseAfterNotification.current = false;
      return;
    }

    if (context.notification?.contextType === 'Query') {
      return;
    }

    if (context.activeTab === PARTICIPANT_REVIEW_CREATE_QUERY && lastItem !== 'dashboard') {
      setActiveTab(PARTICIPANT_REVIEW_CREATE_QUERY);
      setCurrentScreen(PARTICIPANT_REVIEW_CREATE_QUERY);

      if (context.additionalContext?.queryExternalReference) {
        setEditMode(true);
        setIsNew(false);
        setShowQueriesTab(true);
        setShowQueriesTable(false);

        retrieveReview(context.username, context.additionalContext.reviewExternalReference);
        retrieveQuery(context.additionalContext.queryExternalReference);
      } else {
        setEditMode(false);
        setIsNew(true);
        setShowQueriesTable(false);
        setShowQueriesTab(true);

        retrieveReview(context.username, context.additionalContext);
      }

      return;
    }

    if (additionalContextReviewExtRef) {
      setEditMode(true);
      setIsNew(false);
      setActiveTab('participantReviewCreateGeneral');
      setCurrentScreen('participantReviewCreateGeneral');

      retrieveReview(context.username, additionalContextReviewExtRef);

      return;
    }

    setEditMode(true);
    setIsNew(false);
    setActiveTab('participantReviewCreateGeneral');
    setCurrentScreen('participantReviewCreateGeneral');
  }, [context, additionalContextReviewExtRef, retrieveReview, retrieveQuery, setContext]);

  const handleSaveClick = (link) => {
    setCurrentScreen(link);
  };

  const handleUpdate = async (reviewToUpdate) => {
    const [data, error] = await updateReview(context.username, reviewToUpdate);

    if (data) {
      if (data.responseStatus === 'Success') {
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Review Updated',
        });

        await retrieveReview(context.username, data.externalReference);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }

    setSpinnerText(null);
  };

  const userHasViewPermission = useMemo(
    () =>
      hasRoles(
        context,
        mergeRoles(CREATE_PARTICIPANT_REVIEW_ROLES, VIEW_PARTICIPANT_REVIEW_ROLES, EDIT_PARTICIPANT_REVIEW_ROLES)
      ),
    [context]
  );

  const screenMap = new Map();

  screenMap.set(
    'participantReviewCreateGeneral',
    <ParticipantReviewGeneralTab
      context={context}
      setContext={setContext}
      review={review}
      hasQueries={hasQueries}
      navigateTo={navigateTo}
      toolTipsEnabled={toolTipsEnabled}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      handleUpdate={handleUpdate}
      editMode
    />
  );

  screenMap.set(
    'participantReviewCreateQuery',
    <ParticipantQueryCreateTab
      context={context}
      query={query}
      setActiveTab={setActiveTab}
      toolTipsEnabled={toolTipsEnabled}
      setSpinnerText={setSpinnerText}
      handleSaveClick={handleSaveClick}
      showToaster={showToaster}
      editMode={editMode}
      navigateTo={navigateTo}
      isNew={isNew}
      setShowQueriesTab={setShowQueriesTab}
      setShowQueriesTable={setShowQueriesTable}
      visitNotLinked={visitNotLinked}
      retrieveReview={retrieveReview}
      userHasViewPermission={userHasViewPermission}
      review={review}
    />
  );

  screenMap.set(
    'participantQueryTable',
    <ParticipantQueryTable
      context={context}
      setQuery={setQuery}
      setQueries={setQueries}
      setActiveTab={setActiveTab}
      setSpinnerText={setSpinnerText}
      handleSaveClick={handleSaveClick}
      showToaster={showToaster}
      navigateTo={navigateTo}
      setShowQueriesTab={setShowQueriesTab}
      setShowQueriesTable={setShowQueriesTable}
      setIsNew={setIsNew}
      queries={queries}
    />
  );

  const handleBack = () => {
    if (!userHasViewPermission) {
      navigateTo('dashboard');
      return;
    }

    navigateTo('participantReviewsTable');
  };

  const handleNavigationClick = (tab) => {
    if (!userHasViewPermission) return;

    if (tab === PARTICIPANT_REVIEW_CREATE_QUERY) {
      setShowQueriesTab(false);
      setShowQueriesTable(true);
      setActiveTab('participantQueryTable');
      setCurrentScreen('participantQueryTable');
    } else {
      setActiveTab(tab);
      setCurrentScreen(tab);
    }
  };

  return (
    <div className="clients-create-container">
      <div className="clients-create-header-container">
        <BackButton onClick={handleBack} />
        <h2>Edit / View Query Review</h2>
      </div>

      <div className="wizard-content-container">
        <div className="wizard-main-content-container">
          <div className="clients-create-container">
            <div className="clients-create-tab-container">
              <div
                className={
                  'clients-create-tab general ' + (activeTab === 'participantReviewCreateGeneral' ? 'active-tab' : null)
                }
                onClick={() => handleNavigationClick('participantReviewCreateGeneral')}
              >
                General
              </div>

              {showQueriesTab && (
                <div
                  className={
                    'clients-create-tab queries ' + (activeTab === 'participantReviewCreateQuery' ? 'active-tab' : null)
                  }
                  onClick={() => handleNavigationClick('participantReviewCreateQuery')}
                >
                  Queries
                </div>
              )}

              {showQueriesTable && (
                <div
                  className={
                    'clients-create-tab queries ' + (activeTab === 'participantQueryTable' ? 'active-tab' : null)
                  }
                  onClick={() => handleNavigationClick('participantQueryTable')}
                >
                  Queries
                </div>
              )}
            </div>

            <div className={'clients-create-tab-content ' + activeTab}>{screenMap.get(currentScreen)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
